import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import type { DataRouter } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';

import { AppShell } from '@/app/shell-v3/AppShell';
import { AuthGate } from '@/auth/AuthGate';
import { ServerSetupGate } from '@/auth/ServerSetupGate';
import { LoginPage } from '@/pages/LoginPage';
import { SHELL_ROUTE_ID } from '@/state/ui/shellLocationAtoms';

/**
 * A page's module downloads when its route first renders. The login page stays in the entry chunk:
 * a signed-out cold load shows it first.
 */
function lazyPage(load: () => Promise<ComponentType>): LazyExoticComponent<ComponentType> {
    return lazy(async (): Promise<{ default: ComponentType }> => ({ default: await load() }));
}

const AdminBuildsPage = lazyPage(
    async () => (await import('@/pages/AdminBuildsPage')).AdminBuildsPage,
);
const ClipPage = lazyPage(async () => (await import('@/pages/ClipPage')).ClipPage);
const LocalLoginPage = lazyPage(
    async () => (await import('@/pages/LocalLoginPage')).LocalLoginPage,
);
const LogViewerPage = lazyPage(async () => (await import('@/pages/LogViewerPage')).LogViewerPage);
const SetupServerPage = lazyPage(
    async () => (await import('@/pages/SetupServerPage')).SetupServerPage,
);
const ShowcasePage = lazyPage(async () => (await import('@/pages/ShowcasePage')).ShowcasePage);

// The shell owns `/` and normalizes old deep links on mount. Standalone full-screen pages (login,
// showcase) keep their own routes.
// Data router so the shell's history guard (shell-v3/useHistoryGuard.ts) can stop a navigation,
// and so StoreProvider can feed its location into the store (hooks/useRouterBridge.ts).
//
// vite's `base: '/'` keeps asset URLs root-relative (see ui/vite.config.ts).
// Two gates wrap the AppShell route, in order:
//   AuthGate          — no token → /login
//   ServerSetupGate   — 0 servers in active org → /setup-server
//
// /setup-server is gated by AuthGate alone (you must be logged in to claim a server) but NOT by
// ServerSetupGate (otherwise infinite redirect).
export const router: DataRouter = createBrowserRouter([
    { path: '/login', element: <LoginPage /> },
    // Consent popup for media_server cloud sign-in. AuthGate makes an unauthenticated popup pass
    // through /login first (?next preserved).
    { path: '/local-login', element: <AuthGate><LocalLoginPage /></AuthGate> },
    { path: '/setup-server', element: <AuthGate><SetupServerPage /></AuthGate> },
    { path: '/showcase', element: <ShowcasePage /> },
    { path: '/clip', element: <ClipPage /> },
    // Reading one server's log file, opened from the server settings Logs tab. The component
    // segment names the directory the file lives in; a name alone does not, since every component
    // logs under a directory of its own.
    { path: '/logs/:serverId/:component/:name', element: <AuthGate><LogViewerPage /></AuthGate> },
    // Platform-admin only; the server rejects the reads it makes for anyone else.
    { path: '/admin/builds', element: <AuthGate><AdminBuildsPage /></AuthGate> },
    {
        id: SHELL_ROUTE_ID,
        path: '/*',
        element: <AuthGate><ServerSetupGate><AppShell /></ServerSetupGate></AuthGate>,
    },
]);
